import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getOrderListDB, updateOrderListDB } from 'slice/orderSliceDB';
import AdminTable from 'common/AdminTable';
import DateFilter from 'common/DateFilter';
import TruncatedMessage from 'common/TruncatedMessage';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TbShoppingCart, TbCheck, TbX, TbBan, TbEye } from "react-icons/tb";

const STATUS_OPTIONS = ['pending', 'processing', 'dispatch', 'delivered', 'reject', 'cancelByUser', 'complete'];
const STATUS_ALIASES = {
    cancelled: 'cancelByUser',
    canceled: 'cancelByUser',
    cancel: 'cancelByUser',
    cancel_by_user: 'cancelByUser',
    cancelbyuser: 'cancelByUser',
    rejected: 'reject',
    reject: 'reject',
    processing: 'processing',
    dispatched: 'dispatch',
    dispatch: 'dispatch',
    delivered: 'delivered',
    complete: 'complete',
    completed: 'complete',
};

const normalizeStatus = (status) => {
    if (!status) return 'pending';
    const key = String(status).trim().toLowerCase();
    const mapped = STATUS_ALIASES[key] || key;
    return STATUS_OPTIONS.includes(mapped) ? mapped : 'pending';
};

const getRawStatus = (order) =>
    order?.status ?? order?.order_status ?? order?.orderStatus;

const detectItemsKey = (order) => {
    const keys = ['items', 'order_items', 'orderItems', 'cart_items', 'products', 'itemData1'];
    return keys.find((key) => Array.isArray(order?.[key])) || 'items';
};

const extractOrders = (payload) => {
    if (!payload) return [];
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload.orders)) return payload.orders;
    if (Array.isArray(payload.data)) return payload.data;
    if (Array.isArray(payload.order)) return payload.order;
    if (Array.isArray(payload.result)) return payload.result;
    if (payload.data && Array.isArray(payload.data.orders)) return payload.data.orders;
    return [];
};

const buildStatusPayload = (order) => {
    if (!order) return {};
    const status = normalizeStatus(getRawStatus(order));
    return {
        status,
        tracking_url: order.tracking_url,
    };
};

const getOrderItems = (order) => {
    if (!order) {
        return [];
    }
    const key = order?.__itemsKey || detectItemsKey(order);
    const items = order?.[key] || [];
    return Array.isArray(items) ? items : [];
};

const getItemName = (item) => item?.item_name || item?.product?.name || item?.product_name || item?.name || item?.title || '';
const getItemQty = (item) => item?.quantity ?? item?.qty ?? item?.count ?? 1;

const formatWeight = (weightKg) => {
    if (!weightKg && weightKg !== 0) return null;
    const kg = Number(weightKg);
    if (kg >= 1) return `${kg}kg`;
    const grams = Math.round(kg * 1000);
    return `${grams}g`;
};

const formatDate = (value) => {
    if (!value) {
        return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }
    const pad = (num) => String(num).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const OrderList = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const cartData = useSelector((state) => state.orderDB);
    const activeTab = searchParams.get('type') === 'gift' ? 'gift' : 'product';
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [currentProduct, setCurrentProduct] = useState(null);

    useEffect(() => {
        dispatch(getOrderListDB());
    }, [dispatch]);

    useEffect(() => {
        if (showStatusModal) {
            const originalOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = originalOverflow;
            };
        }
        return undefined;
    }, [showStatusModal]);

    const formatItems = (order) => {
        const items = getOrderItems(order);
        if (!items.length) {
            return 'No items';
        }
        return items
            .map((item) => {
                const name = getItemName(item) || 'Item';
                const qty = getItemQty(item);
                return `${name} (x${qty})`;
            })
            .join(', ');
    };

    const allOrders = useMemo(() => extractOrders(cartData?.data || cartData), [cartData]);
    const orders = useMemo(() => {
        return allOrders.filter((o) => {
            const type = (o.order_type || 'product').toLowerCase();
            return activeTab === 'gift' ? type === 'gift' : type === 'product';
        });
    }, [allOrders, activeTab]);

    const handleStatusClick = (product) => {
        setCurrentProduct({ ...product, status: normalizeStatus(getRawStatus(product)) });
        setShowStatusModal(true);
    };

    const handleCloseModal = () => {
        setShowStatusModal(false);
        setCurrentProduct(null);
    };

    const getOrderId = (order) =>
        order?.id || order?.order_id || order?.orderId || order?.order_no || order?._id;

    const handleStatusSave = () => {
        const orderId = getOrderId(currentProduct);
        if (!orderId) {
            handleCloseModal();
            return;
        }
        const payload = buildStatusPayload(currentProduct);
        dispatch(updateOrderListDB({ id: orderId, data: payload }));
        handleCloseModal();
    };

    const tableHeaders = [
        {
            Header: 'ID',
            accessor: 'id',
            Cell: ({ row }) => getOrderId(row.original) || 'N/A'
        },
        { Header: 'Name', accessor: 'customer.name', Cell: ({ row }) => row.original.customer?.name || row.original?.guest_name },
        {
            Header: 'Products',
            accessor: 'order_items',
            Cell: ({ row }) => <TruncatedMessage text={formatItems(row.original)} maxLength={40} />
        },
        { Header: 'Price', accessor: 'total_amount', Cell: ({ row }) => row.original?.total_amount ?? row.original?.total ?? row.original?.amount ?? row.original?.totalAmount ?? '-' },
        { Header: 'Payment', accessor: 'payment_method', Cell: ({ row }) => row.original?.payment_method || row.original?.paymentMode || row.original?.pay_method || '-' },
        {
            Header: 'Transaction ID',
            accessor: 'transaction_id',
            Cell: ({ row }) => {
                const tid = row.original?.transaction_id;
                return tid ? (tid === 'COD' ? <span className="badge badge-success">COD</span> : <span style={{ fontSize: 11 }}>{tid}</span>) : '-';
            }
        },
        { Header: 'Date', accessor: 'createdAt', Cell: ({ row }) => formatDate(row.original.createdAt) },
        {
            Header: 'Address',
            accessor: 'shipping_address',
            Cell: ({ row }) => <TruncatedMessage text={row.original?.shipping_address || row.original?.address || ''} maxLength={30} />
        },
        {
            Header: 'Status',
            accessor: 'status',
            Cell: ({ row }) => {
                const status = normalizeStatus(getRawStatus(row.original) || '');
                let bgColor;
                switch (status) {
                    case 'pending': bgColor = 'bg-warning'; break;
                    case 'processing': bgColor = 'bg-info'; break;
                    case 'dispatch': bgColor = 'bg-primary'; break;
                    case 'delivered': bgColor = 'bg-success'; break;
                    case 'complete': bgColor = 'bg-success'; break;
                    case 'cancelByUser': bgColor = 'bg-danger'; break;
                    case 'reject': bgColor = 'bg-danger'; break;
                    default: bgColor = '';
                }
                const label =
                    status === 'cancelByUser'
                        ? 'Cancel By User'
                        : status
                            ? status.charAt(0).toUpperCase() + status.slice(1)
                            : 'Unknown';
                return (
                    <span className={`badge ${bgColor} text-white p-2`}>
                        {label}
                    </span>
                );
            }
        },
        {
            Header: 'Actions',
            accessor: 'actions',
            Cell: ({ row }) => (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    <button onClick={() => handleStatusClick(row.original)} title="Confirm" style={{ background: 'none', border: 'none', padding: '4px', cursor: 'pointer', lineHeight: 1 }}><TbCheck size={20} color="var(--accent)" /></button>
                    <button
                        onClick={() => navigate(`/billing/details/${getOrderId(row.original)}`)}
                        title="See Details"
                        style={{ background: 'none', border: 'none', padding: '4px', cursor: 'pointer', lineHeight: 1 }}
                    >
                        <TbEye size={20} color="var(--text-muted)" />
                    </button>
                </span>
            ),
        },
    ];

    const orderStats = useMemo(
        () => ({
            pending: orders.filter(o => normalizeStatus(getRawStatus(o)) === 'pending').length || 0,
            complete: orders.filter(o => ['complete', 'delivered'].includes(normalizeStatus(getRawStatus(o)))).length || 0,
            cancel: orders.filter(o => normalizeStatus(getRawStatus(o)) === 'cancelByUser').length || 0,
            reject: orders.filter(o => normalizeStatus(getRawStatus(o)) === 'reject').length || 0,
        }),
        [orders]
    );

    const [filteredOrders, setFilteredOrders] = useState([]);
    const filterColumns = [
        { Header: 'ID', accessor: 'id' },
        { Header: 'Name', accessor: 'customer.name' },
        { Header: 'Price', accessor: 'total_amount' },
        { Header: 'Payment', accessor: 'payment_method' },
        { Header: 'Transaction ID', accessor: 'transaction_id' },
        { Header: 'Date', accessor: 'createdAt' },
        { Header: 'Status', accessor: 'status' },
    ];

    return (
        <>
            <div className="container-fluid mt-5">
                <div className="mainheadig mx-4">
                    <h4 className="text-white font-weight-bold">Order List</h4>
                </div>

                <div className="row mx-4 mb-4">
                    <div className="col-xl-3 col-md-6 mb-4">
                        <div className="card shadow-sm border-0 rounded-lg h-100">
                            <div className="card-body d-flex align-items-center">
                                <div
                                    className="bg-warning text-white me-3 d-flex align-items-center justify-content-center"
                                    style={{ width: "50px", height: "50px", borderRadius: "12px" }}
                                >
                                    <TbShoppingCart size={28} />
                                </div>
                                <div className="flex-grow-1 text-right">
                                    <h6 className="text-muted mb-1">Pending Orders</h6>
                                    <h5 className="fw-bold mb-0">{orderStats.pending}</h5>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-xl-3 col-md-6 mb-4">
                        <div className="card shadow-sm border-0 rounded-lg h-100">
                            <div className="card-body d-flex align-items-center">
                                <div
                                    className="bg-success text-white me-3 d-flex align-items-center justify-content-center"
                                    style={{ width: "50px", height: "50px", borderRadius: "12px" }}
                                >
                                    <TbCheck size={28} />
                                </div>
                                <div className="flex-grow-1 text-right">
                                    <h6 className="text-muted mb-1">Complete Orders</h6>
                                    <h5 className="fw-bold mb-0">{orderStats.complete}</h5>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-xl-3 col-md-6 mb-4">
                        <div className="card shadow-sm border-0 rounded-lg h-100">
                            <div className="card-body d-flex align-items-center">
                                <div
                                    className="bg-danger text-white me-3 d-flex align-items-center justify-content-center"
                                    style={{ width: "50px", height: "50px", borderRadius: "12px" }}
                                >
                                    <TbBan size={28} />
                                </div>
                                <div className="flex-grow-1 text-right">
                                    <h6 className="text-muted mb-1">Cancelled Orders</h6>
                                    <h5 className="fw-bold mb-0">{orderStats.cancel}</h5>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-xl-3 col-md-6 mb-4">
                        <div className="card shadow-sm border-0 rounded-lg h-100">
                            <div className="card-body d-flex align-items-center">
                                <div
                                    className="bg-danger text-white me-3 d-flex align-items-center justify-content-center"
                                    style={{ width: "50px", height: "50px", borderRadius: "12px" }}
                                >
                                    <TbX size={28} />
                                </div>
                                <div className="flex-grow-1 text-right">
                                    <h6 className="text-muted mb-1">Rejected Orders</h6>
                                    <h5 className="fw-bold mb-0">{orderStats.reject}</h5>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="mx-4">
                    <DateFilter
                        data={orders}
                        dateField="createdAt"
                        title={`${activeTab === 'gift' ? 'Gift' : 'Product'} Orders Report`}
                        columns={filterColumns}
                        onFilteredData={setFilteredOrders}
                    />
                </div>

                {orders.length > 0 ? (
                    <AdminTable
                        tableHeaders={tableHeaders}
                        tableData={filteredOrders}
                        getRowId={(row) => getOrderId(row.original)}
                        renderExpandedRow={(order) => {
                            const items = getOrderItems(order);
                            if (!items.length) return <div className="text-muted">No items</div>;
                            return (
                                <table className="table table-sm table-bordered mb-0" style={{ background: '#fff' }}>
                                    <thead>
                                        <tr>
                                            <th style={{ fontSize: 12, padding: '6px 8px' }}>#</th>
                                            <th style={{ fontSize: 12, padding: '6px 8px' }}>Product Name</th>
                                            <th style={{ fontSize: 12, padding: '6px 8px' }}>Product ID</th>
                                            <th style={{ fontSize: 12, padding: '6px 8px' }}>Weight</th>
                                            <th style={{ fontSize: 12, padding: '6px 8px' }}>Qty</th>
                                            <th style={{ fontSize: 12, padding: '6px 8px' }}>Unit Price</th>
                                            <th style={{ fontSize: 12, padding: '6px 8px' }}>Subtotal</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {items.map((item, idx) => {
                                            const itemName = getItemName(item);
                                            const qty = getItemQty(item);
                                            const itemId = item?.product_id || item?.product?.id || item?.id || '-';
                                            const price = item?.price ?? item?.selling_price ?? item?.product?.selling_price ?? 0;
                                            const subtotal = qty * price;
                                            const weightLabel = formatWeight(item?.weight);
                                            return (
                                                <tr key={idx}>
                                                    <td style={{ fontSize: 12, padding: '6px 8px' }}>{idx + 1}</td>
                                                    <td style={{ fontSize: 12, padding: '6px 8px' }}>{itemName || 'Unknown'}</td>
                                                    <td style={{ fontSize: 12, padding: '6px 8px' }}>#{itemId}</td>
                                                    <td style={{ fontSize: 12, padding: '6px 8px' }} className="text-center">
                                                        {weightLabel ? <span className="font-weight-bold">{weightLabel}</span> : '-'}
                                                    </td>
                                                    <td style={{ fontSize: 12, padding: '6px 8px' }} className="text-center">{qty}</td>
                                                    <td style={{ fontSize: 12, padding: '6px 8px' }} className="text-right">₹{Number(price).toLocaleString('en-IN')}</td>
                                                    <td style={{ fontSize: 12, padding: '6px 8px' }} className="text-right">₹{Number(subtotal).toLocaleString('en-IN')}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            );
                        }}
                    />
                ) : (
                    <div className="mx-4 mt-4">No Data Found</div>
                )}
            </div>

            {showStatusModal && currentProduct && (
                <>
                <div className="modal-backdrop fade show"></div>
                <div
                    className="modal fade show"
                    style={{ display: 'block' }}
                    tabIndex="-1"
                    role="dialog"
                    aria-labelledby="statusModalLabel"
                    aria-hidden={false}
                >
                    <div className="modal-dialog modal-dialog-centered" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title" id="statusModalLabel">Update Status</h5>
                                <button type="button" className="close" onClick={handleCloseModal} aria-label="Close">
                                    <span aria-hidden="true">&times;</span>
                                </button>
                            </div>
                            <div className="modal-body">
                                <form>
                                    <div className="form-group">
                                        <label htmlFor="status">Action</label>
                                        <select
                                            className="form-control"
                                            value={normalizeStatus(currentProduct.status) || 'pending'}
                                            onChange={(e) => setCurrentProduct({ ...currentProduct, status: e.target.value })}
                                        >
                                            <option>pending</option>
                                            <option>processing</option>
                                            <option>dispatch</option>
                                            <option>delivered</option>
                                            <option>complete</option>
                                            <option>reject</option>
                                            <option>cancelByUser</option>
                                        </select>
                                    </div>

                                    {currentProduct?.status === 'dispatch' && (
                                        <div className="form-group">
                                            <label htmlFor="trackingUrl">Tracking URL</label>
                                            <input
                                                type="url"
                                                className="form-control"
                                                id="trackingUrl"
                                                placeholder="https://tracking.example.com/..."
                                                value={currentProduct?.tracking_url || ''}
                                                onChange={(e) => setCurrentProduct({ ...currentProduct, tracking_url: e.target.value })}
                                            />
                                        </div>
                                    )}
                                </form>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>Close</button>
                                <button type="button" className="btn addbtn" onClick={handleStatusSave}>Save Status</button>
                            </div>
                        </div>
                    </div>
                </div>
                </>
            )}


        </>
    )
}

export default OrderList;
